import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

type Serializer<T> = {
  parse: (value: string) => T;
  stringify: (value: T) => string;
};

export type UseQueryStateOptions<T> = {
  defaultValue?: T;
  serializer?: Serializer<T>;
  replace?: boolean;
};

const defaultSerializer: Serializer<any> = {
  parse: (value) => value,
  stringify: (value) => String(value),
};

export function useQueryState<T = string>(
  key: string,
  options: UseQueryStateOptions<T> = {},
): [T | undefined, (value: T | undefined | ((prev: T | undefined) => T | undefined)) => void] {
  const { defaultValue, serializer = defaultSerializer, replace = true } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo<T | undefined>(() => {
    const raw = searchParams.get(key);
    if (raw === null) return defaultValue;
    try {
      return serializer.parse(raw);
    } catch {
      return defaultValue;
    }
  }, [searchParams, key, defaultValue, serializer]);

  const setValue = useCallback(
    (next: T | undefined | ((prev: T | undefined) => T | undefined)) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const resolved =
            typeof next === "function"
              ? (next as (p: T | undefined) => T | undefined)(
                  params.get(key) === null
                    ? defaultValue
                    : (serializer.parse(params.get(key) as string) as T),
                )
              : next;

          if (resolved === undefined || resolved === null || resolved === "") {
            params.delete(key);
          } else {
            params.set(key, serializer.stringify(resolved));
          }
          return params;
        },
        { replace },
      );
    },
    [setSearchParams, key, serializer, defaultValue, replace],
  );

  return [value, setValue];
}

export const queryNumberSerializer: Serializer<number> = {
  parse: (v) => Number(v),
  stringify: (v) => String(v),
};

export const queryBooleanSerializer: Serializer<boolean> = {
  parse: (v) => v === "true" || v === "1",
  stringify: (v) => (v ? "true" : "false"),
};

export const queryJsonSerializer = <T,>(): Serializer<T> => ({
  parse: (v) => JSON.parse(v) as T,
  stringify: (v) => JSON.stringify(v),
});
